import { useCallback, useEffect, useRef, useState, type FormEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { currentLocaleTag } from '../../lib/formatting'
import {
  AlertCircle,
  Clock,
  Loader2,
  Pause,
  Pencil,
  Play,
  Plus,
  Square,
  Timer,
  Trash2,
} from 'lucide-react'
import { api, ApiError } from '../../lib/api'
import { confirmDialog, toast } from '../../lib/dialogs'
import Modal from '../ui/Modal'
import { formatCents, parseEurosToCents, centsToEurosInput } from '../../lib/money'
import { useTimer } from '../../contexts/TimerContext'
import { useAuth } from '../../contexts/AuthContext'
import {
  TimeEntryPhaseLabel,
  type CreateTimeEntryInput,
  type TimeEntryDto,
  type TimeEntryListResponse,
  type TimeEntryPhaseT,
  type TimeEntryStatusT,
} from '@jr2os/shared'

interface Props {
  opportunityId: string
  opportunityCode?: string
  canWrite: boolean
}

export default function CaseTimePanel({ opportunityId, opportunityCode, canWrite }: Props) {
  const { t } = useTranslation(['opportunities', 'common'])
  // #8 — Timer global persistido (TimerContext). Independiente del cronómetro
  // local de esta panel; el botón permite arrancarlo/pararlo desde aquí, pero
  // el pill flotante de TimerFloating funciona en cualquier ruta.
  const timer = useTimer()
  const timerActiveHere = timer.isActiveFor(opportunityId)
  const onToggleGlobalTimer = async () => {
    if (timerActiveHere) {
      // Disparamos el modal del pill flotante: stop devuelve el snapshot pero
      // el flujo de guardado está en TimerFloating. Aquí solo cancelamos
      // explícitamente si el usuario lo confirma desde el pill. Como atajo,
      // navegamos al comportamiento del pill llamando a stop() sería un mal
      // UX (perderíamos el modal). Mejor: scroll/foco al pill no es trivial,
      // así que simplemente avisamos al usuario.
      toast.info(t('timePanel.usePillToStop'))
      return
    }
    if (timer.active && timer.active.opportunityId !== opportunityId) {
      if (
        !(await confirmDialog({
          message: t('timePanel.replaceActiveTimer', { code: timer.active.opportunityCode }),
          tone: 'danger',
        }))
      ) {
        return
      }
    }
    timer.start({
      opportunityId,
      opportunityCode: opportunityCode ?? opportunityId.slice(0, 8),
    })
  }

  const [items, setItems] = useState<TimeEntryDto[]>([])
  const [totalMinutes, setTotalMinutes] = useState(0)
  // Totales AGREGADOS en BD sobre todo el filtro (no solo las ≤500 filas que
  // trae `items`): antes el coste se sumaba en cliente sobre la página y se
  // infra-declaraba en silencio en proyectos con muchos registros.
  const [totalCostCents, setTotalCostCents] = useState(0)
  const [totalCount, setTotalCount] = useState(0)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [showForm, setShowForm] = useState<{
    initial: TimeEntryDto | null
    initialMinutes?: number
    initialStart?: string
    initialEnd?: string
  } | null>(null)

  // Cronómetro
  const [timerRunning, setTimerRunning] = useState(false)
  const [timerStart, setTimerStart] = useState<Date | null>(null)
  const [timerElapsed, setTimerElapsed] = useState(0) // segundos acumulados (incluye sesión actual)
  const tickRef = useRef<number | null>(null)

  const reload = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const resp = await api.get<TimeEntryListResponse>(`/time-entries?opportunityId=${opportunityId}`)
      setItems(resp.items)
      setTotalMinutes(resp.totalMinutes)
      setTotalCostCents(resp.totalCostCents)
      setTotalCount(resp.totalCount)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error')
    } finally {
      setLoading(false)
    }
  }, [opportunityId])

  useEffect(() => {
    void reload()
  }, [reload])

  // Tick del cronómetro (cada segundo si está corriendo).
  useEffect(() => {
    if (!timerRunning || !timerStart) return
    tickRef.current = window.setInterval(() => {
      setTimerElapsed(Math.floor((Date.now() - timerStart.getTime()) / 1000))
    }, 1000)
    return () => {
      if (tickRef.current) window.clearInterval(tickRef.current)
    }
  }, [timerRunning, timerStart])

  const startTimer = () => {
    const now = new Date()
    setTimerStart(now)
    setTimerElapsed(0)
    setTimerRunning(true)
  }
  const pauseTimer = () => {
    setTimerRunning(false)
    if (tickRef.current) window.clearInterval(tickRef.current)
  }
  const stopTimer = () => {
    if (!timerStart) return
    const elapsedSec = Math.floor((Date.now() - timerStart.getTime()) / 1000)
    const minutes = Math.max(1, Math.round(elapsedSec / 60))
    pauseTimer()
    setShowForm({
      initial: null,
      initialMinutes: minutes,
      initialStart: timerStart.toISOString(),
      initialEnd: new Date().toISOString(),
    })
    setTimerStart(null)
    setTimerElapsed(0)
  }
  const cancelTimer = () => {
    pauseTimer()
    setTimerStart(null)
    setTimerElapsed(0)
  }

  // En esta vista interna lo relevante es el COSTE REAL (min × coste/h interno),
  // no el importe facturable: es lo que alimenta CAC/margen (petición de Jorge).
  const entryCostCents = (e: TimeEntryDto) =>
    Math.round((e.durationMinutes / 60) * (e.costRateCents ?? 0))

  const onDelete = async (e: TimeEntryDto) => {
    if (!(await confirmDialog({ message: t('timePanel.confirmDelete'), tone: 'danger' }))) return
    try {
      await api.delete(`/time-entries/${e.id}`)
      await reload()
    } catch (err) {
      const body = err instanceof ApiError ? (err.body as { error?: string }) : undefined
      toast.error(body?.error ?? 'Error')
    }
  }

  return (
    <div className="surface p-5">
      <div className="flex items-center justify-between mb-3">
        <h3 className="t-primary font-semibold flex items-center gap-2">
          <Timer size={14} className="text-jr2-700 dark:text-jr2-300" />
          {t('timePanel.title')}
          {items.length > 0 && (
            <span className="text-xs t-faint font-normal">
              ({formatDuration(totalMinutes)} · {t('timePanel.costTag')}{' '}
              {formatCents(totalCostCents)})
            </span>
          )}
        </h3>
        {canWrite && (
          <div className="flex items-center gap-1.5">
            {/* #8 — Botón del timer global persistido (sigue activo entre páginas) */}
            {timerActiveHere ? (
              <button
                type="button"
                onClick={() => void onToggleGlobalTimer()}
                className="px-2.5 py-1 rounded-lg text-xs font-medium text-jr2-800 dark:text-jr2-100 bg-jr2-500/25 hover:bg-jr2-500/35 flex items-center gap-1"
                title={t('timePanel.activeTimerHint')}
              >
                <Square size={11} /> {t('timePanel.stopTimer')}
              </button>
            ) : (
              <button
                type="button"
                onClick={() => void onToggleGlobalTimer()}
                className="px-2.5 py-1 rounded-lg text-xs font-medium text-jr2-700 dark:text-jr2-200 bg-jr2-500/10 hover:bg-jr2-500/20 flex items-center gap-1"
                title={t('timePanel.startGlobalTimerHint')}
              >
                <Play size={11} /> {t('timePanel.startTimer')}
              </button>
            )}
            {!timerRunning && timerStart === null && (
              <button
                type="button"
                onClick={startTimer}
                className="px-2.5 py-1 rounded-lg text-xs font-medium text-emerald-700 dark:text-emerald-200 bg-emerald-500/15 hover:bg-emerald-500/25 flex items-center gap-1"
                title={t('timePanel.startLocalChronoHint')}
              >
                <Play size={11} /> {t('timePanel.start')}
              </button>
            )}
            {timerStart !== null && (
              <>
                <span className="font-mono text-sm t-primary">
                  {formatSeconds(timerElapsed)}
                </span>
                {timerRunning ? (
                  <button
                    type="button"
                    onClick={pauseTimer}
                    className="w-7 h-7 grid place-items-center rounded hover:bg-amber-500/15 t-muted hover:text-amber-700 dark:hover:text-amber-300"
                    title={t('timePanel.pause')}
                  >
                    <Pause size={12} />
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={() => {
                      // reanudar: ajustamos timerStart al backstep
                      setTimerStart(new Date(Date.now() - timerElapsed * 1000))
                      setTimerRunning(true)
                    }}
                    className="w-7 h-7 grid place-items-center rounded hover:bg-emerald-500/15 t-muted hover:text-emerald-700 dark:hover:text-emerald-300"
                    title={t('timePanel.resume')}
                  >
                    <Play size={12} />
                  </button>
                )}
                <button
                  type="button"
                  onClick={stopTimer}
                  className="px-2 py-1 rounded text-xs t-secondary border border-base hover:bg-hover flex items-center gap-1"
                  title={t('timePanel.stopAndSave')}
                >
                  <Square size={10} /> {t('timePanel.stop')}
                </button>
                <button
                  type="button"
                  onClick={cancelTimer}
                  className="text-xs t-faint hover:text-rose-700 dark:hover:text-rose-300"
                  title={t('common:actions.cancel')}
                >
                  ✕
                </button>
              </>
            )}
            {timerStart === null && (
              <button
                type="button"
                onClick={() => setShowForm({ initial: null })}
                className="px-2.5 py-1 rounded-lg text-xs font-medium text-jr2-700 dark:text-jr2-200 bg-jr2-500/15 hover:bg-jr2-500/25 flex items-center gap-1"
                title={t('timePanel.manualHint')}
              >
                <Plus size={12} /> {t('timePanel.manual')}
              </button>
            )}
          </div>
        )}
      </div>

      {error && (
        <div className="text-xs text-rose-700 dark:text-rose-300 flex items-center gap-1 mb-2">
          <AlertCircle size={11} /> {error}
        </div>
      )}

      {loading ? (
        <div className="t-faint text-xs flex items-center gap-1.5">
          <Loader2 size={11} className="animate-spin" /> {t('common:loading')}
        </div>
      ) : items.length === 0 ? (
        <p className="text-xs t-faint">{t('timePanel.empty')}</p>
      ) : (
        <ul className="divide-y divide-base/40 text-sm">
          {items.map((e) => (
            <li key={e.id} className="py-2 flex items-start gap-2">
              <div className="flex-1 min-w-0">
                <div className="t-primary">{e.description}</div>
                <div className="flex items-center gap-2 text-[11px] t-muted mt-0.5 flex-wrap">
                  <span>{new Date(e.performedOn).toLocaleDateString(currentLocaleTag())}</span>
                  <span className="font-mono">{formatDuration(e.durationMinutes)}</span>
                  <span className="font-mono" title={t('timePanel.costRowTitle')}>
                    {formatCents(entryCostCents(e))}
                  </span>
                  <span>· {e.userName}</span>
                  <PhaseBadge phase={e.phase} />
                  <StatusBadge status={e.status} />
                </div>
              </div>
              {canWrite && e.status !== 'BILLED' && (
                <div className="flex items-center gap-0.5 shrink-0">
                  <button
                    type="button"
                    onClick={() => setShowForm({ initial: e })}
                    className="w-6 h-6 grid place-items-center rounded hover:bg-hover t-muted hover:text-jr2-700 dark:hover:text-jr2-200"
                    title={t('common:actions.edit')}
                  >
                    <Pencil size={11} />
                  </button>
                  <button
                    type="button"
                    onClick={() => void onDelete(e)}
                    className="w-6 h-6 grid place-items-center rounded hover:bg-rose-500/10 t-muted hover:text-rose-400"
                    title={t('common:actions.delete')}
                  >
                    <Trash2 size={11} />
                  </button>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}

      {totalCount > items.length && (
        <p className="text-[11px] t-faint mt-2">
          {t('timePanel.truncated', { shown: items.length, total: totalCount })}
        </p>
      )}

      {showForm && (
        <TimeEntryFormModal
          opportunityId={opportunityId}
          initial={showForm.initial}
          initialMinutes={showForm.initialMinutes}
          initialStart={showForm.initialStart}
          initialEnd={showForm.initialEnd}
          onClose={() => setShowForm(null)}
          onSaved={async () => {
            setShowForm(null)
            await reload()
          }}
        />
      )}
    </div>
  )
}

function StatusBadge({ status }: { status: TimeEntryStatusT }) {
  const { t } = useTranslation()
  const STYLES: Record<TimeEntryStatusT, string> = {
    UNBILLED: 'bg-amber-500/10 text-amber-700 dark:text-amber-300 border-amber-500/40',
    BILLED: 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border-emerald-500/40',
    NON_BILLABLE: 'bg-slate-500/10 text-slate-600 dark:text-slate-300 border-slate-500/30',
  }
  return (
    <span className={`inline-flex px-1.5 py-0.5 rounded text-[10px] border ${STYLES[status]}`}>
      {t('enums:TimeEntryStatus.' + status)}
    </span>
  )
}

function PhaseBadge({ phase }: { phase: TimeEntryPhaseT }) {
  const { t } = useTranslation()
  const STYLES: Record<TimeEntryPhaseT, string> = {
    PREVENTA: 'bg-purple-500/10 text-purple-700 dark:text-purple-200 border-purple-500/40',
    EJECUCION: 'bg-jr2-500/10 text-jr2-700 dark:text-jr2-200 border-jr2-500/30',
  }
  return (
    <span className={`inline-flex px-1.5 py-0.5 rounded text-[10px] border ${STYLES[phase]}`}>
      {t('enums:TimeEntryPhase.' + phase)}
    </span>
  )
}

function TimeEntryFormModal({
  opportunityId,
  initial,
  initialMinutes,
  initialStart,
  initialEnd,
  onClose,
  onSaved,
}: {
  opportunityId: string
  initial: TimeEntryDto | null
  initialMinutes?: number
  initialStart?: string
  initialEnd?: string
  onClose: () => void
  onSaved: () => void | Promise<void>
}) {
  const { t } = useTranslation(['opportunities', 'common'])
  const auth = useAuth()
  const currentUserId = auth.user?.id ?? null
  // El coste interno (€/h) determina el margen (rentabilidad/CAC): solo un ADMIN
  // lo ve/edita, y solo un ADMIN puede imputar tiempo por otra persona.
  const isAdmin = auth.user?.role === 'ADMIN'

  const [description, setDescription] = useState(initial?.description ?? '')
  const [performedOn, setPerformedOn] = useState(
    (initial?.performedOn ?? new Date().toISOString()).slice(0, 10)
  )
  const [durationText, setDurationText] = useState(
    minutesToHM(initialMinutes ?? initial?.durationMinutes ?? 0)
  )
  const [phase, setPhase] = useState<TimeEntryPhaseT>(initial?.phase ?? 'EJECUCION')
  // F45 — Los registros nuevos nacen "no facturable" (JR2 registra tiempo para
  // coste interno; facturar por horas es la excepción → se activa desmarcando).
  const [nonBillable, setNonBillable] = useState(
    initial ? initial.status === 'NON_BILLABLE' : true
  )
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // F45 — Persona a la que se imputa el tiempo. Solo un ADMIN puede cambiarla
  // (registrar horas de otra persona). En edición la persona es fija.
  const [people, setPeople] = useState<
    Array<{ id: string; fullName: string; costPerHourCents: number | null }>
  >([])
  const [selectedUserId, setSelectedUserId] = useState<string>(
    initial?.userId ?? currentUserId ?? ''
  )

  // Coste/hora (€) — se trae del perfil de la persona; editable. Solo un ADMIN lo
  // envía; para el resto el backend usa el coste del perfil de la persona.
  const [costEuros, setCostEuros] = useState(
    initial?.costRateCents != null ? centsToEurosInput(initial.costRateCents) : ''
  )
  // Tarifa facturable (€/h) — solo relevante si el registro NO es "no facturable".
  const [billableEuros, setBillableEuros] = useState(
    initial && initial.status !== 'NON_BILLABLE' && initial.hourlyRateCents
      ? centsToEurosInput(initial.hourlyRateCents)
      : ''
  )

  // Personas seleccionables (solo admin, solo en alta) + prellenado del coste.
  useEffect(() => {
    if (!isAdmin || initial) return
    let cancelled = false
    api
      .get<{
        users: Array<{
          id: string
          fullName: string
          role: string
          isActive: boolean
          costPerHourCents: number | null
        }>
      }>('/users')
      .then((r) => {
        if (cancelled) return
        const opts = r.users
          .filter((u) => u.isActive && ['ADMIN', 'CONSULTOR', 'ANALISTA'].includes(u.role))
          .map((u) => ({
            id: u.id,
            fullName: u.fullName,
            costPerHourCents: u.costPerHourCents ?? null,
          }))
        setPeople(opts)
        const meCost = opts.find((o) => o.id === selectedUserId)?.costPerHourCents ?? null
        if (meCost != null) setCostEuros((prev) => prev || centsToEurosInput(meCost))
      })
      .catch(() => setPeople([]))
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin, initial])

  const onChangePerson = (id: string) => {
    setSelectedUserId(id)
    const p = people.find((x) => x.id === id)
    setCostEuros(p?.costPerHourCents != null ? centsToEurosInput(p.costPerHourCents) : '')
  }

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault()
    const minutes = hmToMinutes(durationText)
    if (minutes <= 0) {
      setError(t('timePanel.errors.invalidDuration'))
      return
    }
    if (!description.trim()) {
      setError(t('timePanel.errors.missingDescription'))
      return
    }
    setBusy(true)
    setError(null)
    // F45 — Coste (solo admin) y, si es facturable, la tarifa €/h manual.
    const costPatch =
      isAdmin && costEuros.trim() ? { costRateCents: parseEurosToCents(costEuros) } : {}
    const billablePatch =
      !nonBillable && billableEuros.trim()
        ? { hourlyRateCents: parseEurosToCents(billableEuros) }
        : {}
    try {
      if (initial) {
        await api.patch(`/time-entries/${initial.id}`, {
          description: description.trim(),
          performedOn: new Date(performedOn).toISOString(),
          durationMinutes: minutes,
          phase,
          nonBillable,
          ...costPatch,
          ...billablePatch,
        })
      } else {
        const payload: CreateTimeEntryInput = {
          opportunityId,
          description: description.trim(),
          performedOn: new Date(performedOn).toISOString(),
          durationMinutes: minutes,
          startedAt: initialStart ?? null,
          endedAt: initialEnd ?? null,
          phase,
          // Solo un admin imputa por otra persona; si es él mismo, no se envía.
          ...(isAdmin && selectedUserId && selectedUserId !== currentUserId
            ? { userId: selectedUserId }
            : {}),
          ...costPatch,
          ...billablePatch,
          nonBillable,
        }
        await api.post('/time-entries', payload)
      }
      await onSaved()
    } catch (err) {
      const body = err instanceof ApiError ? (err.body as { error?: string }) : undefined
      setError(body?.error ?? (err instanceof Error ? err.message : 'Error'))
    } finally {
      setBusy(false)
    }
  }

  const input =
    'w-full bg-transparent border border-base rounded px-2 py-1.5 text-sm t-primary focus:outline-none focus:border-jr2-500/50'

  return (
    <Modal open onClose={onClose} title={initial ? t('timePanel.form.editTitle') : t('timePanel.form.newTitle')}>
      <form onSubmit={onSubmit} className="space-y-3">
        <label className="block">
          <span className="text-xs t-muted">{t('timePanel.form.descriptionLabel')}</span>
          <input
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder={t('timePanel.form.descriptionPlaceholder')}
            className={input}
            required
            autoFocus
          />
        </label>
        <div className="grid grid-cols-2 gap-3">
          <label className="block">
            <span className="text-xs t-muted">{t('timePanel.form.date')}</span>
            <input
              type="date"
              value={performedOn}
              onChange={(e) => setPerformedOn(e.target.value)}
              className={input}
              required
            />
          </label>
          <label className="block">
            <span className="text-xs t-muted">
              {t('timePanel.form.duration')}
            </span>
            <input
              value={durationText}
              onChange={(e) => setDurationText(e.target.value)}
              placeholder="1:30"
              className={`${input} font-mono`}
              required
            />
          </label>
        </div>
        <label className="block">
          <span className="text-xs t-muted">{t('timePanel.form.phase')}</span>
          <select
            value={phase}
            onChange={(e) => setPhase(e.target.value as TimeEntryPhaseT)}
            className={input}
          >
            {Object.keys(TimeEntryPhaseLabel).map((k) => (
              <option key={k} value={k}>
                {t('enums:TimeEntryPhase.' + k)}
              </option>
            ))}
          </select>
        </label>
        {/* F45 — Persona (solo admin). En alta es un selector; en edición, fija. */}
        {isAdmin && !initial && people.length > 0 && (
          <label className="block">
            <span className="text-xs t-muted">{t('timePanel.form.personLabel')}</span>
            <select
              value={selectedUserId}
              onChange={(e) => onChangePerson(e.target.value)}
              className={input}
            >
              {people.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.fullName}
                  {p.id === currentUserId ? ` ${t('timePanel.form.personSelfSuffix')}` : ''}
                </option>
              ))}
            </select>
          </label>
        )}
        {isAdmin && initial && (
          <p className="text-xs t-muted">
            {t('timePanel.form.personLabel')}:{' '}
            <span className="t-secondary">{initial.userName}</span>
          </p>
        )}
        {/* Coste/hora (solo admin): se trae del perfil de la persona, editable. */}
        {isAdmin && (
          <label className="block">
            <span className="text-xs t-muted">{t('timePanel.form.costLabel')}</span>
            <input
              value={costEuros}
              onChange={(e) => setCostEuros(e.target.value)}
              placeholder="0,00"
              className={`${input} font-mono`}
            />
            <span className="text-[10px] t-faint">{t('timePanel.form.costHint')}</span>
          </label>
        )}
        <label className="flex items-center gap-2 text-sm t-secondary">
          <input
            type="checkbox"
            checked={nonBillable}
            onChange={(e) => setNonBillable(e.target.checked)}
            className="accent-jr2-500"
          />
          {t('timePanel.form.nonBillable')}
        </label>
        {/* Tarifa facturable: solo si NO es "no facturable" (facturar por horas). */}
        {!nonBillable && (
          <label className="block">
            <span className="text-xs t-muted">{t('timePanel.form.billableRateLabel')}</span>
            <input
              value={billableEuros}
              onChange={(e) => setBillableEuros(e.target.value)}
              placeholder="0,00"
              className={`${input} font-mono`}
            />
            <span className="text-[10px] t-faint">{t('timePanel.form.billableRateHelp')}</span>
          </label>
        )}

        {error && <p className="text-xs text-rose-700 dark:text-rose-300">{error}</p>}

        <div className="flex justify-end gap-2 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="px-3 py-1.5 rounded-lg text-sm t-secondary border border-base hover:bg-hover"
          >
            {t('common:actions.cancel')}
          </button>
          <button
            type="submit"
            disabled={busy}
            className="px-3 py-1.5 rounded-lg text-sm font-semibold text-white bg-jr2-600 hover:bg-jr2-700 disabled:opacity-50 flex items-center gap-1.5"
          >
            {busy && <Loader2 size={14} className="animate-spin" />}
            {initial ? t('common:actions.save') : t('timePanel.form.register')}
          </button>
        </div>
      </form>
    </Modal>
  )
}

function formatDuration(minutes: number): string {
  const h = Math.floor(minutes / 60)
  const m = minutes % 60
  return `${h}h ${String(m).padStart(2, '0')}m`
}

function formatSeconds(secs: number): string {
  const h = Math.floor(secs / 3600)
  const m = Math.floor((secs % 3600) / 60)
  const s = secs % 60
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`
}

function minutesToHM(minutes: number): string {
  if (!minutes) return ''
  const h = Math.floor(minutes / 60)
  const m = minutes % 60
  return `${h}:${String(m).padStart(2, '0')}`
}

function hmToMinutes(text: string): number {
  if (!text.trim()) return 0
  const t = text.trim().toLowerCase().replace(/\s*h$/, '').replace(',', '.')
  if (t.includes(':')) {
    const [h, m] = t.split(':')
    return (parseInt(h ?? '0', 10) || 0) * 60 + (parseInt(m ?? '0', 10) || 0)
  }
  const decimal = parseFloat(t)
  if (!Number.isFinite(decimal) || decimal <= 0) return 0
  return Math.round(decimal * 60)
}

void Clock // suprime warning
